import { alpha, ThemeOptions } from '@mui/material';
import { appTheme } from './appTheme.ts';
import { grey } from './utils/colors.ts';
import { defaultBorderRadius, spacing } from './utils/constants.ts';
import { glassBackdrop, glassBackdropSheet, lightGlassBackdrop } from './utils/glassBackdrop.ts';

export const mobileTheme: ThemeOptions = {
    ...appTheme,
    palette: {
        ...appTheme.palette,
        type: 'dark',
    },
    components: {
        MuiCssBaseline: { ...appTheme.components?.MuiCssBaseline },
        MuiTypography: {
            styleOverrides: {
                root: {
                    color: grey[0],
                },
                caption: {
                    color: alpha(grey[0], 0.65),
                    letterSpacing: '0.015em',
                },
                overline: {
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    letterSpacing: '0.05em',
                    color: grey[0],
                    textTransform: 'uppercase',
                    margin: 0,
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                badge: {
                    fontWeight: 'bold',
                    bottom: 2,
                    right: 2,
                },
                colorInfo: {
                    backgroundColor: grey[900],
                    color: grey[0],
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    display: 'flex',
                    alignItems: 'stretch',
                    justifyContent: 'stretch',
                    border: 'none',

                    '&:before': {
                        border: 'none',
                    },
                },
                select: {
                    color: grey[0],
                    fontWeight: 'bold',
                    borderRadius: 1.75 * defaultBorderRadius,
                    outline: 'none',
                    display: 'flex',
                    alignItems: 'center',
                },
                icon: {
                    color: grey[0],
                },
                outlined: {
                    padding: `${1.5 * spacing}px ${3 * spacing}px`,
                    border: `1.5px solid ${alpha(grey[0], 0.25)}`,
                },
                filled: {
                    padding: `${1.5 * spacing}px ${2 * spacing}px`,
                    ...glassBackdrop,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: {
                    borderColor: 'transparent !important',
                    border: 'none !important',
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    fill: grey[0],
                    color: grey[0],

                    '&.Mui-checked': {
                        fill: grey[0],
                        color: grey[0],
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    padding: `${1.5 * spacing}px ${2.5 * spacing}px`,
                    color: grey[0],
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    minWidth: 24,
                },
                contained: {
                    ...glassBackdrop,

                    '&:hover': {
                        ...glassBackdrop,
                    },
                },
                outlined: {
                    border: `1.5px solid ${alpha(grey[0], 0.25)}`,
                    borderRadius: 1.75 * defaultBorderRadius,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                contained: {
                    ...glassBackdrop,
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                switchBase: {
                    color: grey[300],
                    '&.Mui-checked': {
                        color: grey[0],
                    },
                    '&.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: grey[0],
                    },
                    '&.Mui-checked .MuiSwitch-thumb': {
                        color: grey[0],
                    },
                },
                track: {
                    backgroundColor: alpha(grey[0], 0.5),
                },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    color: grey[0],
                    border: `1.5px solid ${alpha(grey[0], 0.25)}`,
                    borderRadius: 1.75 * defaultBorderRadius,
                    padding: 0.5 * spacing,
                },
                firstButton: {
                    borderRadius: 1.25 * defaultBorderRadius,
                    border: 'none',
                },
                middleButton: {
                    borderRadius: 1.25 * defaultBorderRadius,
                    border: 'none',
                },
                lastButton: {
                    borderRadius: 1.25 * defaultBorderRadius,
                    border: 'none',
                },
            },
            variants: [
                {
                    props: { size: 'small' },
                    style: {
                        border: 'none',

                        '& .MuiToggleButton-root': {
                            fontSize: '0.75rem',
                        },
                    },
                },
            ],
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    border: `1.5px solid ${grey[800]}`,
                    color: grey[300],
                    padding: `${1 * spacing}px ${2 * spacing}px`,
                    fontSize: '0.875rem',
                    fontWeight: 'bold',
                    letterSpacing: '0.03em',

                    '&.Mui-selected': {
                        background: alpha(grey[0], 0.25),
                        color: grey[0],

                        '&:hover': {
                            background: alpha(grey[0], 0.25),
                            color: grey[0],
                        },
                    },
                },
            },
        },
        MuiSlider: {
            styleOverrides: {
                thumb: {
                    backgroundColor: grey[0],
                },
                rail: {
                    backgroundColor: alpha(grey[0], 0.4),
                },
                track: {
                    backgroundColor: grey[0],
                },
                markLabel: {
                    color: grey[500],
                },
                valueLabel: {},
            },
        },
        MuiPopover: {
            styleOverrides: {
                root: {
                    zIndex: 10000,
                },
            },
        },
    },
    custom: {
        ...appTheme.custom,
        glassBackdrop,
        lightGlassBackdrop,
        glassBackdropSheet,
        snapGradient: {
            backgroundColor: 'rgba(0,0,0,0)',
            tickColor: alpha(grey[0], 0.5),
            highlightColor: alpha(grey[0], 1),
        },
    },
};
